import React, {useState, useEffect} from 'react';
import {useNavigate, useParams, useLocation} from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import {
  CustomInput,
  CustomSelect,
  CustomToggle,
  CustomFileUpload,
  CustomCodeEditor
} from '../Components/CustomFormElements';
import Button from '../Components/Buttons';
import {AlertProvider, useAlert} from '../Components/AlertContext';
import {ConfirmationProvider, useConfirmation} from '../Components/ConfirmationContext';
import PageLoader from '../Components/PageLoader';
import {isEmpty} from '../utils/validationUtil';
import {ArtifactApiProvider, useArtifactApi} from "../Services/ArtifactApiProvider";
import {useCachedBlobUrl} from "../Services/useBlobDownloader";
import { v4 as UUID } from 'uuid';
import ArtifactParameters from './ArtifactParameters';
import Card from '../Components/Card';

function ArtifactDetail({canEdit}) {
  const navigate = useNavigate();
  const {organizationId, artifactId} = useParams();
  const {showAlert} = useAlert();
  const {showConfirmation} = useConfirmation();

  const {createArtifact, updateArtifactById, getArtifactById, generateTestPDF} = useArtifactApi();
  const {getArtifactUrl, getPublicArtifactUrl, getPublicArtifactAddressUrl} = useCachedBlobUrl()

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isNew, setIsNew] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);

  const [artifactName, setArtifactName] = useState();
  const [artifactType, setArtifactType] = useState();
  const [isActive, setIsActive] = useState(true);
  const [isPublic, setIsPublic] = useState(true);
  const [publicUrl, setPublicUrl] = useState('');

  const [file, setFile] = useState(null);
  const [existingFile, setExistingFile] = useState(null);
  const [removeFileFlag, setRemoveFileFlag] = useState(false);

  const [parameters, setParameters] = useState([]);
  const [showPdfCard, setShowPdfCard] = useState(false);
  const [generatedPdf, setGeneratedPdf] = useState(null);

  const defaultFormats = ['image/png', 'image/jpeg', 'image/heic', 'application/pdf'];
  const templateFormats = ['text/html'];

  useEffect(() => {
    return () => {
      if (generatedPdf && generatedPdf.url) {
        window.URL.revokeObjectURL(generatedPdf.url);
      }
    };
  }, [generatedPdf]);

  useEffect(() => {
    const func = async () => {
      setLoading(true);
      try {
        if (artifactId !== 'new-artifact') {
          const artifactUrl = await getArtifactUrl(artifactId);
          const response = await getArtifactById(artifactId);
          setArtifactName(response.name);
          setIsActive(response.is_active);
          setArtifactType(response.type);
          setIsPublic(response.is_public);
          setParameters(response.parameters);

          if (response.file_name) {
            setExistingFile({
              filename: response.file_name,
              url: artifactUrl,
              size: response.size
            });
          }
        }
      } catch (error) {
        setError('Error fetching artifact data');
        console.error('Error fetching artifact data:', error);
      } finally {
        setLoading(false);
      }
    }
    func();
  }, [artifactId]);

  useEffect(() => {
    if (artifactType === 'template') {
      setIsPublic(false);
    }
  }, [artifactType]);

  useEffect(() => {
  const fetchPublicUrl = async () => {
    if (isActive && isPublic && artifactId !== 'new-artifact') {
      try {
        const url = await getPublicArtifactAddressUrl(artifactId);
        setPublicUrl(url);
      } catch (err) {
        console.error("Error fetching public artifact URL:", err);
        setPublicUrl(null);
      }
    } else {
      setPublicUrl(null);
    }
  };

  fetchPublicUrl();
}, [artifactId, getPublicArtifactUrl]);

  useEffect(() => {
    setIsNew(artifactId === 'new-artifact');
    setIsDisabled(!canEdit);
  }, [artifactId])

  console.log("existingFile", existingFile)

  function onSetFiles(files) {
    if (!files || files.length === 0) {
      setFile(null);
      return;
    }
    const selectedFile = files[0];
    setFile(selectedFile);
    setRemoveFileFlag(false);
    setExistingFile(null);
  }

  const onSave = async () => {
    if (isEmpty(artifactName)) {
      showAlert('fail', 'Artifact name is required');
      return;
    }
    if (isEmpty(artifactType)) {
      showAlert('fail', 'Artifact type is required');
      return;
    }

    const formData = new FormData();
    formData.append('name', artifactName);
    formData.append('type', artifactType);
    formData.append('is_active', isActive ? 'true' : 'false');
    formData.append('is_public', isPublic ? 'true' : 'false');

    if (file) {
      formData.append('file', file, file.name);
    }

    if (isNew && artifactType === 'template') {
      formData.append(
        'parameters',
        JSON.stringify(parameters.map(({ key, name, description }) => ({ key, name, description })))
      );
    }

    setLoading(true);
    try {
      if (isNew) {
        await createArtifact(formData);
        showAlert('success', 'Artifact created successfully');
      } else {
        await updateArtifactById(artifactId, formData);
        showAlert('success', 'Artifact updated successfully');
      }
      navigate(`/organizations/${organizationId}/artifacts`);
    } catch (error) {
      console.error('Error saving artifact:', error);
      showAlert('fail', 'Failed to save artifact');
    } finally {
      setLoading(false);
    }
  }

  const handleOpenPdfCard = () => {
    if (!parameters || parameters.length === 0) {
      showAlert("fail", "No parameters defined for this template");
      return;
    }
    setShowPdfCard(true);
  };

  const onSavePdfGeneration = async (inputValues) => {
    setShowPdfCard(false);
    setLoading(true);

    if (generatedPdf && generatedPdf.url) {
        window.URL.revokeObjectURL(generatedPdf.url);
        setGeneratedPdf(null);
    }

    const params = inputValues.reduce((acc, item) => {
      acc[item.name] = item.value;
      return acc;
    }, {});

    try {
      const pdf_url = await generateTestPDF(artifactId, params);

      const fileName = `test-${artifactName || "NoName"}.pdf`;
      setGeneratedPdf({
        url: pdf_url,
        name: fileName
      });

      showAlert("success", "Test PDF generated successfully");

    } catch (error) {
      console.error("Error generating test PDF:", error);
      showAlert("fail", "Failed to generate test PDF");
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/artifacts`);
  };

  if (loading) {
    return <PageLoader/>;
  }

  return (
    <div className="container">
      {showPdfCard && (
        <Card
          parameters={parameters}
          onSave={onSavePdfGeneration}
          onCancel={() => setShowPdfCard(false)}
        />
      )}

      <Breadcrumbs/>
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <h2 className='h2-title'>{artifactName}</h2>
          <div className='gap d-flex flex-row-to-col flex-column'>
            <div className='int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr'>
              <h4 className='h4-title'>Main Settings</h4>

              <div className='input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Artifact name</div>
                  <div className='small-font'>Enter artifact name</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomInput
                    id="chat-name"
                    label="Artifact Name"
                    placeholder="Enter artifact name"
                    type="text"
                    icon={<i className="fa-solid fa-comments"></i>}
                    value={artifactName}
                    onChange={(id, value) => setArtifactName(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className='input-row input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Artifact Type</div>
                  <div className='small-font'>Select artifact type</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomSelect
                    id='artifact-type'
                    label="Artifact Type"
                    options={[
                      { value: 'default', label: 'Default' },
                      { value: 'template', label: 'Template' },
                    ]}
                    value={artifactType}
                    onChange={(value) => setArtifactType(value)}
                    multiSelect={false}
                    disabled={isDisabled || !isNew}
                  />
                </div>
              </div>
              {existingFile && !file && (
                <div className='input-row input-block d-flex flex-row justify-between'>
                  <div className='input-description'>
                    <div className='input-title'>Uploaded File</div>
                  </div>
                  <div className='align-wrapper d-flex align-center'>
                    <div className="existing-file mt-2">
                      <a href={existingFile.url} target="_blank" rel="noreferrer">
                        {existingFile.filename}
                      </a>
                      <span>
                        {" "}({existingFile.size} MB)
                      </span>
                    </div>
                  </div>
                </div>
              )}

              <div className='input-row input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Artifact file</div>
                  <div className='small-font'>Select or replace artifact file</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomFileUpload
                    multiple={false}
                    maxFileSize={25 * 1024 * 1024}
                    allowedFormats={artifactType === 'template' ? templateFormats : defaultFormats}
                    showUploadedFiles={true}
                    allowFileDeletion={true}
                    showAlert={showAlert}
                    showConfirmation={showConfirmation}
                    onFileSelect={(files) => onSetFiles(files)}
                  />
                </div>
              </div>

              <div className='input-row input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Artifact private</div>
                  <div className='small-font'>Set artifact to be public or private</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomToggle
                    id="toggle-status-public"
                    label={isPublic ? 'Public' : 'Private'}
                    checked={isPublic}
                    onChange={(id, value) => setIsPublic(value)}
                    disabled={artifactType === 'template'}
                  />
                </div>
              </div>

              <div className='input-row input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Activate artifact</div>
                  <div className='small-font'>Set artifact to be active or inactive</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomToggle
                    id="toggle-status-active"
                    label={isActive ? 'Active' : 'Inactive'}
                    checked={isActive}
                    onChange={(id, value) => setIsActive(value)}
                    disabled={false}
                  />
                </div>
              </div>

              {isActive && isPublic && (
                  <div className="input-block">
                    <h2 className='input-title'>Public URL</h2>
                    <CustomCodeEditor
                        id="public-url"
                        value={publicUrl || ''}
                        boxtitle=''
                        wrapText={true}
                        language="javascript"
                        editable={false}
                        height='auto'
                        highlightActiveLine={false}
                        lightTheme='githubLight'
                        darkTheme='dracula'
                        lineNumbers={false}
                        foldGutter={false}
                    />
                  </div>
              )}

            </div>

            <div className='buttons-container d-flex justify-end'>
              {generatedPdf && (
                  <div className="existing-file mt-2 d-flex mr-2">
                    <a
                        href={generatedPdf.url}
                        download={generatedPdf.name}
                        className="d-flex align-center gap-2"
                    >
                      {generatedPdf.name}
                    </a>
                  </div>
              )}

              {!isNew && artifactType === 'template' && (
                  <Button variant="ghost" size="medium" onClick={handleOpenPdfCard}>
                  Generate test PDF
                </Button>
              )}
              <Button variant='primary' size='medium' disabled={isDisabled} onClick={onSave}>
                Save
              </Button>
              <Button variant='danger' size='medium' onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>

        {artifactType === 'template' && (
          <ArtifactParameters
            parameters={parameters}
            setParameters={setParameters}
            canEdit={canEdit}
            isNew={isNew}
            artifactId={artifactId}
            artifactName={artifactName}
            artifactType={artifactType}
            setLoading={setLoading}
            showAlert={showAlert}
            showConfirmation={showConfirmation}
            updateArtifactById={updateArtifactById}
          />
        )}
      </div>
    </div>
  )
}

function ArtifactWrapper({canEdit}) {
  return (
    <AlertProvider>
      <ConfirmationProvider>
        <ArtifactApiProvider>
          <ArtifactDetail canEdit={canEdit}/>
        </ArtifactApiProvider>
      </ConfirmationProvider>
    </AlertProvider>
  );
}

export default ArtifactWrapper;
